import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { apiClient, useAuthStore } from '@indipia/shared'
import { Alert, Combobox, Counter, Page, PageHeader, TextLink } from '@indipia/ui-core'
import type { NavFn, DirtyHandle } from '../pages/SchedulerPage'
import { useCommonCodes } from '../context/CommonCodesContext'
import { ErrorBanner } from '../components/AsyncState'
import { SettingsSaveBar } from '../components/SettingsSaveBar'
import {
  applyMode,
  buildSettingsPayload,
  diffOrderSettings,
  draftMode,
  settingsErrorMessage,
  metricFieldsOf,
  toDraft,
  vendorMetricParts,
  type OrderMode,
  type OrderSettingDraft,
  type OrderSettingServer,
} from '../constants/orderSettings.logic'
import { ORDER_MESSAGE } from '../constants/materialOrder.logic'
import { indexVendorMetrics, vendorMetricsQueryOptions } from '../hooks/useVendorMetrics'

const MSG = ORDER_MESSAGE.settings

type Props = { navigate: NavFn; onDirtyChange?: (h: DirtyHandle | null) => void }

interface OrderSetting {
  workTypeCompId: number | null
  workTypeCode: string
  workTypeName: string
  orderYn: string | null
  orderLeadDays: number | null
  vendorId: number | null
  /** SIF-1 '자재파트너가 시공' 방식(ORDER_YN 'Y' + CONSTRUCT 'Y') */
  constructByVendorYn?: string | null
}

// /schedule/vendors 응답 — 주거래처 후보 필터용(공종코드 다중 workTypes 포함)
// 참고지표(avgRating·orderCount)는 후보 드롭다운에 표시한다. 값이 없으면 해당 조각을 생략한다(0%·0.0 채우기 금지).
interface MatVendor {
  vendorId: number
  vendorName: string
  workTypes: string[]
  avgRating?: number | null   // 평균별점(없으면 별 조각 생략)
  reviewCount?: number | null // 후기 수
  orderCount?: number | null  // 거래 횟수
  replyWithinTwoHoursPercent?: number | null // SV2 지표(없거나 null 이면 그 조각 생략)
  onTimePercent?: number | null
  liftAvailPercent?: number | null
}

const SAVED_VISIBLE_MS = 2000

function toServerRows(list: OrderSetting[], base?: Map<string, OrderSettingServer>): Map<string, OrderSettingServer> {
  const rows = new Map(base ?? [])
  list.forEach(s => rows.set(s.workTypeCode, {
    workTypeCode: s.workTypeCode,
    orderYn: s.orderYn ?? null,
    orderLeadDays: s.orderLeadDays ?? null,
    vendorId: s.vendorId ?? null,
    constructByVendorYn: s.constructByVendorYn ?? null,
  }))
  return rows
}

export function OrderSettingsView({ navigate: _navigate, onDirtyChange }: Props) {
  const routerNavigate = useNavigate()
  const user = useAuthStore(s => s.user)
  const { codes } = useCommonCodes()
  // 스케줄 전용 행정 공정(세대동의서청구 등, scheduleOnlyYn='Y')은 발주 대상이 아니라 제외
  const workTypes = (codes?.workTypes ?? []).filter(w => w.scheduleOnlyYn !== 'Y')
  const codeOrder = workTypes.map(w => w.workTypeCode)

  const [serverRows, setServerRows] = useState<Map<string, OrderSettingServer>>(new Map())
  const [baseDrafts, setBaseDrafts] = useState<Map<string, OrderSettingDraft>>(new Map())
  const [drafts,     setDrafts]     = useState<Map<string, OrderSettingDraft>>(new Map())
  const [vendors,    setVendors]    = useState<MatVendor[]>([])
  const [loading,    setLoading]    = useState(true)
  const [error,      setError]      = useState('')
  const [loadError,  setLoadError]  = useState(false)
  const [reloadKey,  setReloadKey]  = useState(0)
  const [saving,     setSaving]     = useState(false)
  const [saved,      setSaved]      = useState(false)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 요청 헤더(X-Comp-Id·X-User-Id)는 게이트웨이가 주입한다(로컬 개발 우회는 shared client.ts 인터셉터) — 여기서 싣지 않는다.
  // SV2 지표 — 주거래처 옵션 지표에 병합. 실패해도 지표 줄만 생략(오류 화면 없음)
  const metricsQ = useQuery(vendorMetricsQueryOptions(user?.compId))
  const metricsById = indexVendorMetrics(metricsQ.data)

  /** 서버 값 → 기준값·초안. 행 없음(orderYn=null)은 켜짐으로 표시(D6), 리드타임은 값을 만들어 채우지 않음 */
  const applyServer = (rows: Map<string, OrderSettingServer>, resetAll: boolean, only: string[] = []) => {
    setServerRows(rows)
    const base = new Map<string, OrderSettingDraft>()
    codeOrder.forEach(c => base.set(c, toDraft(rows.get(c))))
    setBaseDrafts(base)
    setDrafts(prev => {
      if (resetAll) return new Map(base)
      const next = new Map(prev)
      only.forEach(c => { const b = base.get(c); if (b) next.set(c, b) })
      return next
    })
  }

  useEffect(() => {
    setLoading(true)
    setLoadError(false)
    apiClient.get<{ data: { orderSettings: OrderSetting[] } }>('/schedule/settings')
      .then(res => {
        const list = res.data?.data?.orderSettings
        if (!Array.isArray(list)) throw new Error('INVALID_RESPONSE_SHAPE')   // 빈 값을 '전체 초기값'으로 위장하지 않음
        applyServer(toServerRows(list), true)
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false))

    // 주거래처 후보 — 공종코드로 필터하기 위해 전체 거래처를 한 번 조회(실패 시 빈 목록)
    apiClient.get<{ data: MatVendor[] }>('/schedule/vendors')
      .then(res => setVendors(res.data?.data ?? []))
      .catch(() => setVendors([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey])

  useEffect(() => () => { if (savedTimerRef.current) clearTimeout(savedTimerRef.current) }, [])

  const patchDraft = (code: string, fn: (d: OrderSettingDraft) => OrderSettingDraft) => {
    setSaved(false)
    setDrafts(prev => {
      const cur = prev.get(code) ?? toDraft(serverRows.get(code))
      return new Map(prev).set(code, fn(cur))
    })
  }

  // Counter가 min/max 범위의 정수만 넘기므로 별도 파싱·보정 없이 바로 반영
  const setLeadTime = (code: string, v: number) => patchDraft(code, d => ({ ...d, orderLeadDays: v }))
  // 방식을 바꿔도 리드타임·주거래처 값은 보관하고 '별도발주'로 돌아오면 복원한다(R-112).
  const setMode = (code: string, mode: OrderMode) =>
    patchDraft(code, d => applyMode(d, mode))
  // 주거래처를 지정하면 그 공종은 발주하는 공종이므로 발주여부를 자동 Y로 켠다
  const setVendorId = (code: string, v: number | null) =>
    patchDraft(code, d => ({ ...d, vendorId: v, orderYn: v != null ? 'Y' : d.orderYn }))

  // 주거래처 후보: 해당 공종을 workTypes에 포함하는 거래처
  const vendorCandsFor = (code: string): MatVendor[] =>
    vendors.filter(v => (v.workTypes ?? []).includes(code))

  // 주거래처 콤보 닫힌 상태 표시 — '업체명 · ★평점 · 거래 N회 · 납기준수 N%'(펼친 옵션과 같은 지표 계산, 지표 없으면 이름만)
  const vendorClosedLabel = (v: MatVendor): string => {
    const m = vendorMetricParts({ ...v, ...metricFieldsOf(metricsById.get(v.vendorId)) })
    const parts = [...(m.rating != null ? [`★${m.rating}`] : []), ...m.rest]
    return parts.length > 0 ? `${v.vendorName} · ${parts.join(' · ')}` : v.vendorName
  }

  // 리드타임 예시용 데모 착공일(6/20). 간트에 등록된 실제 착공일이 없을 때 쓰는 고정값.
  const demoStart = new Date(2026, 5, 20)
  const fmtMD = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`
  const notifyDate = (days: number) => {
    const d = new Date(demoStart)
    d.setDate(d.getDate() - days)
    return d
  }

  const changedCodes = diffOrderSettings(baseDrafts, drafts)
  const payload = buildSettingsPayload(codeOrder, serverRows, drafts, changedCodes)
  const changedCount = changedCodes.length

  const handleReset = () => {
    setError('')
    setSaved(false)
    setDrafts(new Map(baseDrafts))
  }

  /** 실패 시 오류를 표시하고 던진다(이탈 확인창이 이동을 막도록) */
  const handleSave = async () => {
    setError('')
    if (payload.items.length === 0) return
    setSaving(true)
    try {
      const res = await apiClient.patch<{ data: { orderSettings: OrderSetting[] } }>(
        '/schedule/settings', { orderSettings: payload.items })
      const returned = res.data?.data?.orderSettings
      if (!returned) throw new Error('empty-settings-response')
      applyServer(toServerRows(returned, serverRows), false, payload.items.map(i => i.workTypeCode))
      setSaved(true)
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
      savedTimerRef.current = setTimeout(() => setSaved(false), SAVED_VISIBLE_MS)
    } catch (e) {
      setError(settingsErrorMessage(e))
      throw e
    } finally {
      setSaving(false)
    }
  }

  // 이탈 보호 — 바꾼 공종이 있을 때만 부모에 보고. subject 로 이탈 확인 문구의 대상 이름을 넘긴다
  const saveRef = useRef(handleSave)
  saveRef.current = handleSave
  const resetRef = useRef(handleReset)
  resetRef.current = handleReset
  const reportDirty = useCallback((h: DirtyHandle | null) => { onDirtyChange?.(h) }, [onDirtyChange])
  useEffect(() => {
    if (changedCount === 0) { reportDirty(null); return }
    const handle: DirtyHandle = {
      count: changedCount,
      subject: MSG.dirtySubject,
      save: async () => { await saveRef.current() },
      discard: () => resetRef.current(),
    }
    reportDirty(handle)
  }, [changedCount, reportDirty])
  useEffect(() => () => reportDirty(null), [reportDirty])
  useEffect(() => {
    if (changedCount === 0) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [changedCount])

  return (
    <div style={{ minHeight: '100%' }}>
      {loading ? (
        <div aria-busy="true" style={{ textAlign: 'center', padding: 'var(--spacing-10) 0', color: 'var(--shell-hint)', fontSize: 13 }}>불러오는 중...</div>
      ) : (
        <Page width="lg">
          <PageHeader title="공종별 발주 설정(납기·주거래처)" subtitle="공종별로 발주 · 시공 방식 · 발주 리드타임(착공일 기준) · 주거래처를 설정하세요." />

          {loadError && (
            <div style={{ marginBottom: 16 }}>
              <ErrorBanner message={MSG.loadFail} onRetry={() => setReloadKey(k => k + 1)} />
            </div>
          )}

          {/* 안내(인포) — 타이틀 아래 (기획 lt-note 원문) */}
          <Alert kind="info" className="mb-4">
            <span>
              발주 알림은 <strong>착공일 기준</strong> 리드타임만큼 앞서 보내요. <strong>발주없음</strong>이면 리드타임·알림·주거래처가 모두 꺼져요.<br />
              <strong>자재파트너가 시공</strong>이면 주거래처가 시공까지 맡아 시공파트너 섭외(일정확인 알림톡)를 하지 않아요.<br />
              {MSG.modeChangeNotice}
            </span>
          </Alert>

          {/* 방식 선택박스는 좁게, 펼친 목록(옵션+한 줄 설명)은 내용 폭만큼 넓게 — 이 화면 한정(Combobox 목록 폭 기본 = 트리거 폭) */}
          <style>{`
            .field.h5-mode-combo { width: 176px; max-width: 176px; }
            .field.h5-mode-combo .combo .menu { width: max-content; min-width: 100%; max-width: 360px; }
          `}</style>

          <div style={{ background: 'var(--shell-surface)', border: 'var(--border-width-hairline) solid var(--shell-border)', borderRadius: 16, overflow: 'hidden', marginBottom: 12 }}>
            {/* 테이블 */}
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--shell-bg)' }}>
                  <th style={{ padding: 'var(--spacing-04) var(--spacing-05)', textAlign: 'left', fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', letterSpacing: '-.2px' }}>공종명</th>
                  <th style={{ padding: 'var(--spacing-04) var(--spacing-05)', textAlign: 'left', fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', letterSpacing: '-.2px', minWidth: 160 }}>{MSG.modeTableHead}</th>
                  <th style={{ padding: 'var(--spacing-04) var(--spacing-05)', textAlign: 'left', fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', letterSpacing: '-.2px' }}>발주 리드타임 <span style={{ fontWeight: 500, color: 'var(--shell-hint)' }}>(착공일 기준)</span></th>
                  <th style={{ padding: 'var(--spacing-04) var(--spacing-05)', textAlign: 'left', fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', letterSpacing: '-.2px' }}>주거래처</th>
                </tr>
              </thead>
              <tbody>
                {workTypes.map((w, i) => {
                  const color   = w.workTypeColor ?? 'var(--shell-on-surface-2)'
                  const draft   = drafts.get(w.workTypeCode) ?? toDraft(serverRows.get(w.workTypeCode))
                  const days    = draft.orderLeadDays
                  const mode    = draftMode(draft)
                  const vid     = draft.vendorId   // 별도발주가 아니어도 보관 중인 값(다시 별도발주로 돌아오면 복원)
                  const cands  = vendorCandsFor(w.workTypeCode)
                  const candMap = new Map(cands.map(v => [String(v.vendorId), v]))
                  const current = vid != null ? candMap.get(String(vid)) : undefined
                  return (
                    <tr key={w.workTypeCode} style={{ borderTop: i > 0 ? '1px solid var(--shell-border)' : 'none' }}>
                      <td style={{ padding: 'var(--spacing-05)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
                          <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--shell-on-surface)' }}>{w.workTypeName}</span>
                        </div>
                      </td>
                      <td style={{ padding: 'var(--spacing-04) var(--spacing-05)', minWidth: 160 }}>
                        <Combobox
                          value={mode}
                          displayValue={
                            mode === 'SEPARATE' ? MSG.modeSeparate
                              : mode === 'NONE' ? MSG.modeNone
                                : MSG.modeVendorConstruct
                          }
                          options={[
                            { value: 'SEPARATE', label: MSG.modeSeparate },
                            { value: 'NONE', label: MSG.modeNone },
                            { value: 'VENDOR_CONSTRUCT', label: MSG.modeVendorConstruct },
                          ]}
                          placeholder={MSG.modeColHead}
                          aria-label={MSG.modeSelectLabel(w.workTypeName)}
                          onChange={val => setMode(w.workTypeCode, val as OrderMode)}
                          className="!mb-0 h5-mode-combo"
                          renderOption={(opt) => {
                            const selected = opt.value === mode
                            const desc = opt.value === 'SEPARATE' ? MSG.modeSeparateDesc
                              : opt.value === 'NONE' ? MSG.modeNoneDesc
                                : MSG.modeVendorConstructDesc
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <div style={{ fontSize: 'var(--type-body-sm-size)', lineHeight: 1.3, fontWeight: 700, color: selected ? 'var(--gt-primary)' : 'var(--shell-on-surface)' }}>
                                  {selected ? '✓ ' : ''}{opt.label}
                                </div>
                                <div style={{ fontSize: 'var(--type-caption-size)', lineHeight: 1.3, color: 'var(--shell-on-surface-2)' }}>{desc}</div>
                              </div>
                            )
                          }}
                        />
                      </td>
                      {mode !== 'NONE' ? (
                        <>
                          {/* 리드타임 — Counter sm. '착공 N 일 전' + 예시 날짜 */}
                          <td style={{ padding: 'var(--spacing-04) var(--spacing-05)' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {days == null ? (
                                <div role="alert" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--status-error-500)' }}>{MSG.leadMissing}</div>
                              ) : (
                                <>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', whiteSpace: 'nowrap' }}>착공</span>
                                    <Counter size="sm" value={days} onChange={v => setLeadTime(w.workTypeCode, v)} min={1} max={99} />
                                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--shell-on-surface)', whiteSpace: 'nowrap' }}>일 전</span>
                                  </div>
                                  <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)' }}>
                                    예) 착공 {fmtMD(demoStart)} → {fmtMD(notifyDate(days))} 알림
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                          {/* 주거래처 — 리치 드롭다운(별점·거래횟수). 후보 0곳이면 등록 링크 */}
                          <td style={{ padding: 'var(--spacing-04) var(--spacing-05)', minWidth: 220 }}>
                            {cands.length === 0 ? (
                              <div style={{ lineHeight: 1.5 }}>
                                <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)' }}>이 공종의 거래처가 아직 없어요</div>
                                <TextLink onClick={() => routerNavigate('/vendors/new')} style={{ display: 'inline-block', marginTop: 2, fontSize: 'var(--type-caption-size)' }}>
                                  자재파트너 등록 →
                                </TextLink>
                              </div>
                            ) : (
                              <>
                                <Combobox
                                  value={vid != null ? String(vid) : ''}
                                  displayValue={current ? vendorClosedLabel(current) : ''}
                                  options={[
                                    { value: '', label: '주거래처 없음' },
                                    ...cands.map(v => ({ value: String(v.vendorId), label: vendorClosedLabel(v) })),
                                  ]}
                                  emptyText="해당 공종 거래처가 없습니다"
                                  placeholder="주거래처 선택"
                                  onChange={val => setVendorId(w.workTypeCode, val ? Number(val) : null)}
                                  className="!mb-0 !max-w-none"
                                  renderOption={(opt) => {
                                    if (opt.value === '') {
                                      return <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-on-surface-2)' }}>주거래처 없음</span>
                                    }
                                    const v = candMap.get(opt.value)
                                    if (!v) return opt.label
                                    const m = vendorMetricParts({ ...v, ...metricFieldsOf(metricsById.get(v.vendorId)) })
                                    return (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <div style={{ fontSize: 'var(--type-body-sm-size)', lineHeight: 1.3 }}>
                                          <b style={{ fontWeight: 700, color: 'var(--shell-on-surface)' }}>{v.vendorName}</b>
                                        </div>
                                        <div style={{ fontSize: 'var(--type-caption-size)', lineHeight: 1.3, color: 'var(--shell-on-surface-2)' }}>
                                          {m.empty ? MSG.noHistory : (
                                            <>
                                              {m.rating != null && (
                                                <>
                                                  <span style={{ color: 'var(--status-warning-500)' }}>★</span>
                                                  <b style={{ fontWeight: 700, color: 'var(--shell-on-surface)' }}>{m.rating}</b>
                                                </>
                                              )}
                                              {m.rest.length > 0 && `${m.rating != null ? ' · ' : ''}${m.rest.join(' · ')}`}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    )
                                  }}
                                />
                                {mode === 'VENDOR_CONSTRUCT' ? (
                                  <div style={{ marginTop: 4, fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--gt-primary)' }}>{MSG.modeVendorInfo}</div>
                                ) : (
                                  <div style={{ marginTop: 4, fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)' }}>후보 {cands.length}곳</div>
                                )}
                              </>
                            )}
                          </td>
                        </>
                      ) : (
                        // 발주없음 — 리드타임·주거래처 셀을 병합한 점선 안내박스
                        <td colSpan={2} style={{ padding: 'var(--spacing-04) var(--spacing-05)' }}>
                          <div style={{ padding: 'var(--spacing-03) var(--spacing-04)', background: 'var(--shell-bg)', border: '1px dashed var(--shell-border)', borderRadius: 10, fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>
                            <span>{MSG.modeNoneInfo}</span>
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {!loadError && (
            <SettingsSaveBar
              changedCount={changedCount}
              saving={saving}
              saved={saved}
              error={error || null}
              skippedNotice={payload.skipped > 0 ? MSG.skipped(payload.skipped) : null}
              onSave={() => { void handleSave().catch(() => undefined) }}
            />
          )}
        </Page>
      )}
    </div>
  )
}
